
import React, { ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';

interface StatCardsProps {
  loading: boolean;
  loadingPageStats?: boolean;
  loadingPlatformStats?: boolean;
  loadingBrowserStats?: boolean;
  loadingOSStats?: boolean;
  loadingSessionMetrics?: boolean;
  totalEvents: number;
  pageStats: any[];
  platformStats: any[];
  browserStats?: any[];
  osStats?: any[];
  sessionMetrics?: {
    avg_session_duration_seconds: number;
    bounce_rate: number;
    avg_pages_per_session: number;
  } | null;
  selectedRange: number;
}

const StatCards = ({ 
  loading, 
  loadingPageStats = false, 
  loadingPlatformStats = false, 
  loadingBrowserStats = false, 
  loadingOSStats = false, 
  loadingSessionMetrics = false,
  totalEvents, 
  pageStats, 
  platformStats, 
  browserStats = [], 
  osStats = [],
  sessionMetrics,
  selectedRange 
}: StatCardsProps) => {
  
  // Function to render a loading card
  const renderLoadingCard = () => (
    <Card className="animate-pulse">
      <CardHeader className="pb-2">
        <div className="h-4 bg-muted rounded w-24 mb-1"></div>
        <div className="h-8 bg-muted rounded w-16"></div>
      </CardHeader>
      <CardContent>
        <div className="h-4 bg-muted rounded w-32"></div>
      </CardContent>
    </Card>
  );

  // Function to render a card with data
  const renderDataCard = (title: string, value: ReactNode, description: string, isLoading: boolean) => {
    if (isLoading) {
      return renderLoadingCard();
    }
    
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
          <p className="text-3xl font-bold">{value}</p>
        </CardHeader>
        <CardContent>
          <CardDescription>{description}</CardDescription>
        </CardContent>
      </Card>
    );
  };

  // Format duration to human readable format
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  // Find the top browser if data exists and not loading
  const topBrowser = !loadingBrowserStats && browserStats.length > 0
    ? browserStats.sort((a, b) => b.count - a.count)[0].browser
    : 'Loading...';

  // Find the top OS if data exists and not loading
  const topOS = !loadingOSStats && osStats.length > 0
    ? osStats.sort((a, b) => b.count - a.count)[0].os
    : 'Loading...';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {renderDataCard(
          "Total Events", 
          loading ? <Skeleton className="h-8 w-16" /> : totalEvents.toLocaleString(), 
          `Past ${selectedRange} days`,
          loading
        )}
        
        {renderDataCard(
          "Unique Pages", 
          loadingPageStats ? <Skeleton className="h-8 w-16" /> : pageStats.length, 
          "Pages with activity",
          loadingPageStats
        )}
        
        {renderDataCard(
          "Top Browser", 
          loadingBrowserStats ? <Skeleton className="h-8 w-16" /> : topBrowser, 
          "Most common browser",
          loadingBrowserStats
        )}
        
        {renderDataCard(
          "Top OS", 
          loadingOSStats ? <Skeleton className="h-8 w-16" /> : topOS, 
          "Most common operating system",
          loadingOSStats
        )}
      </div>

      {/* Session Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {renderDataCard(
          "Avg Session Duration",
          loadingSessionMetrics || !sessionMetrics || sessionMetrics.avg_session_duration_seconds == null
            ? <Skeleton className="h-8 w-16" /> 
            : formatDuration(sessionMetrics.avg_session_duration_seconds),
          "Average time spent per session",
          loadingSessionMetrics
        )}
        
        {renderDataCard(
          "Bounce Rate",
          loadingSessionMetrics || !sessionMetrics || sessionMetrics.bounce_rate == null
            ? <Skeleton className="h-8 w-16" /> 
            : `${Number(sessionMetrics.bounce_rate).toFixed(1)}%`,
          "Single-page sessions",
          loadingSessionMetrics
        )}
        
        {renderDataCard(
          "Pages per Session",
          loadingSessionMetrics || !sessionMetrics || sessionMetrics.avg_pages_per_session == null
            ? <Skeleton className="h-8 w-16" /> 
            : Number(sessionMetrics.avg_pages_per_session).toFixed(1),
          "Average pages viewed",
          loadingSessionMetrics
        )}
      </div>
    </div>
  );
};

export default StatCards;
